import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Image,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiHeartFill, RiStore2Line } from "@remixicon/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FavoriteItem } from "../../constants/Favorite";
import useCustomToast from "../../lib/useCustomToast";
import { useCartStore } from "../../store/useCartStore";
import { useBorderColor, useDarkWhiteColor } from "../../themes/colors";
import { axiosBuyer } from "../../utils/AxiosInstance";
import formatNumber from "../../utils/helperFunction";

interface Props extends StackProps {
  data: FavoriteItem;
  onRefresh: () => void;
}

export const FavoriteProductCard = ({ data, onRefresh, ...rest }: Props) => {
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [isCartLoading, setIsCartLoading] = useState<boolean>(false);
  const { fetchTotalProductsInCart } = useCartStore();
  const navigate = useNavigate();
  const bgBnw = useDarkWhiteColor();
  const borderColor = useBorderColor();
  const toast = useCustomToast();

  const goToDetailProduct = () => {
    navigate(`/product/${data.prefix}/${data.slug}`);
  };

  const deleteFavorite = (e: any) => {
    e.stopPropagation();

    setIsDeleteLoading(true);

    const payload = {
      store_id: data.store_id,
      product_id: data.product_id,
      variant_id: data.variant_id,
    };

    axiosBuyer
      .post(`/v1/buyer/favorite`, payload)
      .then((res) => {
        onRefresh();
        toast({
          title: res.data.message,
          status: "success",
        });
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setIsDeleteLoading(false);
      });
  };

  const addToCart = (e: any) => {
    e.stopPropagation();

    setIsCartLoading(true);

    const payload = {
      store_id: data.store_id,
      product_id: data.product_id,
      variant_id: data.variant_id,
    };

    axiosBuyer
      .post(`/v1/buyer/cart`, payload)
      .then((res) => {
        fetchTotalProductsInCart();

        toast({
          title: res.data.message,
          status: "success",
        });
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setIsCartLoading(false);
      });
  };

  return (
    <Box
      w={"100%"}
      gap={0}
      bg={bgBnw}
      borderWidth={"1px"}
      borderColor={borderColor}
      borderRadius={"md"}
      align={"stretch"}
      cursor={"pointer"}
      onClick={goToDetailProduct}
      {...rest}
    >
      <Image
        className="ratio-1-1"
        src={
          data.main_picture
            ? `${process.env.REACT_APP_MEDIA_URL}${data.main_picture}`
            : ""
        }
        alt={data.variantName || "Image Product"}
        borderTopRightRadius={"md"}
        borderTopLeftRadius={"md"}
        objectFit={"cover"}
        fallbackSrc="/assets/svg/illustration_empty.svg"
        objectPosition={"center"}
      />

      <VStack p={2} align={"stretch"} gap={0.5}>
        <Text textStyle={"b-reg-sb"} noOfLines={1} textOverflow={"ellipsis"}>
          {data.name}
        </Text>

        <Text
          mt={-0.5}
          textStyle={"b-sm"}
          noOfLines={1}
          variant={"secondary"}
          textOverflow={"ellipsis"}
        >
          {`Varian ${data.variantName}`}
        </Text>

        <HStack fontSize={[10, null, 12]} mt={1} alignItems={"center"}>
          <Icon as={RiStore2Line} opacity={0.6} />
          <Text
            textStyle={"b-reg"}
            variant={"secondary"}
            noOfLines={1}
            textOverflow={"ellipsis"}
          >
            {data.store}
          </Text>
        </HStack>

        <Text
          textStyle={"h-reg-title"}
          color={"red.400"}
          mt={1}
        >{`Rp  ${formatNumber(data.price)}`}</Text>

        <HStack w={"100%"} mt={2} gap={1}>
          <IconButton
            aria-label="Favorite-button"
            icon={<Icon as={RiHeartFill} />}
            colorScheme="red"
            variant={"outline"}
            size={["xs", null, "sm"]}
            borderRadius={["sm", null, "md"]}
            isDisabled={isDeleteLoading ? true : false}
            onClick={(e) => deleteFavorite(e)}
          />

          <Button
            flex={1}
            size={["xs", null, "sm"]}
            variant={"outline"}
            colorScheme="red"
            borderRadius={["sm", null, "md"]}
            isDisabled={isCartLoading ? true : false}
            onClick={(e) => addToCart(e)}
          >
            Keranjang
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};
